// UX96 (DEC-356; docs/build-briefs/UX96-floor-header-one-name-centered-logo.md): the floor header's center.
// The header is a three-column grid with equal side columns, so the logo is centered whatever the left group
// holds. It stays centered only while the right group fits inside its own column, so the right group is compacted
// in a fixed order until it does. These are the pure parts of that decision; main.ts's fitTopbarBrand measures the
// page and applies the answer. The logo is never moved off center: a header too narrow for the most compact right
// group keeps the logo centered and lets the group scroll in its own lane (the UX15 rule).

/** The lockup image's width over its height (viewBox 1263 x 346 in design/brand/snipsip-logo.svg). */
export const TOPBAR_LOGO_ASPECT = 1263 / 346;
/** The mark's width over its height (viewBox 266 x 346 in design/brand/snipsip-mark.svg). */
export const TOPBAR_MARK_ASPECT = 266 / 346;

/** "full": the lockup. "mark": the martini glass alone, when the lockup would not leave the right group room. */
export type TopbarCenter = "full" | "mark";

/** How far the right group is compacted. Each level adds to the one before: 1 tightens the gaps, 2 shows the
 * card reader as a short "Reader" chip, 3 shows the pricing model as a short chip, 4 shows the user as initials, 5 trims the horizontal padding of the
 * 86 list button, the account menu and the header itself. */
export const TOPBAR_COMPACT_LEVELS = [0, 1, 2, 3, 4, 5] as const;
export type TopbarCompactLevel = (typeof TOPBAR_COMPACT_LEVELS)[number];

export interface TopbarLayout {
  center: TopbarCenter;
  level: TopbarCompactLevel;
  /** False when even the mark with the most compact right group does not fit beside the center. */
  fits: boolean;
}

/** What the page measures at one compaction level: the header's content width, its column gap and the right
 * group's width (the last level trims the header's own padding and gap too, so all three move with the level). */
export interface TopbarMeasure {
  inner: number;
  gap: number;
  right: number;
}

/** `logo` and `mark` are the two widths and `measureAt` reads the page at a level. A centered piece of width c
 * leaves each side column (inner - c) / 2 - gap. Order of preference: the lockup at the least compaction that fits;
 * else the mark at the least compaction that fits; else the mark at the most compaction, which does not fit. */
export function chooseTopbarLayout(
  logo: number,
  mark: number,
  measureAt: (level: TopbarCompactLevel) => TopbarMeasure,
): TopbarLayout {
  for (const [center, width] of [
    ["full", logo],
    ["mark", mark],
  ] as const) {
    for (const level of TOPBAR_COMPACT_LEVELS) {
      const { inner, gap, right } = measureAt(level);
      if (right <= (inner - width) / 2 - gap) return { center, level, fits: true };
    }
  }
  return { center: "mark", level: 5, fits: false };
}
